import agencyApi from 'api/agencyApi';
import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import AgencyHelper from 'features/Agency/agencyHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import ModelConfigInvestorTableColumn from 'features/ManageInvestor/components/ModalConfigTableColumn';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import BreadcrumbHelper from 'general/components/CustomBreadcrumb/BreadcrumbHelper';
import DropdownFilterTable from 'general/components/DropdownFilterTable';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSButton, {
  KTBSButtonColor,
  KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTBSCard from 'general/components/OtherKeenComponents/KTBSCard';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker, { dateFilter, getDateFilterOption } from 'general/components/Others/CustomDateRangePicker';
import EmptyView from 'general/components/Others/EmptyView';
import MenuActionDropdown from 'general/components/Others/MenuActionDropdown';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import ToastHelper from 'general/helpers/ToastHelper';
import Global from 'general/utils/Global';
import Utils from 'general/utils/Utils';
import useAppConfig from 'hooks/useAppConfig';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as ExcelJS from 'exceljs';
import UIHelper from 'general/helpers/UIHelper';

const defaultColumnIds = [
  'Mã NĐT',
  'Loại nhà đầu tư',
  'Đối tượng quản lý',
  'INVESTOR_NAME',
  'EMAIL',
  'PHONE',
  'STATUS',
  'COUNTRY',
  'Địa chỉ',
  'CREATEDBY',
  'Ngày tạo',
  'MST/CCCD',
  'TOTAL_REVENUE',
  'TOTAL_INVESTMENT'
];

function InvestorGroupListScreen(props) {
  // MARK: ---- params ----
  const { t } = useTranslation();
  const router = useRouter();
  const { isGettingData, groupList, groupTotal } = useSelector((state) => state.agency);
  const [showModelConfigInvestorTableColumn, setShowModelConfigInvestorTableColumn] =
    useState(false);
  const [filter, setFilter] = useState(Global.g_filterInvestorGroupList);
  const [filterDropdown, setFilterDropdown] = useState(Global.g_filterInvestorGroupList);

  // khu vuc
  const areaTree = useSelector((state) => state.app.areaTree);
  const areaProvinceList = useMemo(() => {
    return ChargeHelper.Utils.getAreaListByType([areaTree], 'P');
  }, [areaTree]);
  const areaProvinceOptions = useMemo(() => {
    return _.map(areaProvinceList, (item) => {
      return {
        name: item.name,
        value: item.areaId,
      };
    });
  }, [areaProvinceList]);
  const [selectedAreaProvince, setSelectedAreaProvince] = useState();
  const areaDistrictList = useMemo(() => {
    if (!selectedAreaProvince) return [];
    return ChargeHelper.Utils.getSubAreaListByParentId([areaTree], selectedAreaProvince?.areaId);
  }, [areaTree, selectedAreaProvince]);
  const areaDistrictOptions = useMemo(() => {
    return _.map(areaDistrictList, (item) => {
      return {
        name: item.name,
        value: item.areaId,
      };
    });
  }, [areaDistrictList]);
  const [selectedAreaDistrict, setSelectedAreaDistrict] = useState();
  useEffect(() => {
    const areaId = filterDropdown.areaId;
    if (Utils.isValid(areaId)) {
      const area = ChargeHelper.Utils.getAreaById([areaTree], areaId);
      if (area?.typeCode === 'P') {
        setSelectedAreaProvince(area);
      } else {
        setSelectedAreaDistrict(area);
        const parentAreaId = area?.parentId;
        if (Utils.isValid(parentAreaId)) {
          setSelectedAreaProvince(ChargeHelper.Utils.getAreaById([areaTree], parentAreaId));
        }
      }
    }
  }, [filterDropdown?.areaId, areaTree]);

  const [loading, setLoading] = useState(false);
  const { user, permissionList } = useSelector((state) => state.auth);
  const app = useSelector((state) => state.app);
  const adminRole =
    user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
  const operatorId = useMemo(() => {
    return app?.appConfig?.operators?.[0]?.operatorId ?? null;
  }, [app]);
  const commissionRateSale = useAppConfig()?.commissionRateSale;
  const commissionRateGroup = useAppConfig()?.commissionRateGroup;
  const commissionRateLandlord = useAppConfig()?.commissionRateLandlord;

  const fullColumns = useMemo(() => {
    return [
      {
        id: 'Mã NĐT',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Mã NĐT')}
          </span>
        ),
        width: '140px',
        selector: (row) => row?.agencyCode ?? row.agencyId,
        style: tableStyle.columns.normal,
      },
      {
        id: 'INVESTOR_NAME',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Tên NĐT HTKD')}
          </span>
        ),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row.name ?? ''}>
              <span>{row.name ?? ''}</span>
            </KTTooltip>
          );
        },
        minWidth: '220px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'Loại nhà đầu tư',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Loại nhà đầu tư')}
          </span>
        ),
        selector: (row) => t(row.type),
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'Đối tượng quản lý',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Đối tượng quản lý')}
          </span>
        ),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.parentAgency?.name}>
            <span
              className="cursor-pointer hover-opacity-60"
              onClick={() => {
                DealerHelper.navigateToParentAgency(row?.parentAgency);
              }}
            >
              {row?.parentAgency?.name}
            </span>
          </KTTooltip>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'PHONE',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('phone')}
          </span>
        ),
        width: '140px',
        selector: (row) => row.phone ?? '',
        style: tableStyle.columns.normal,
      },
      {
        id: 'EMAIL',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('email')}
          </span>
        ),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row?.email ?? 'N/A'}>
              <span>{row?.email ?? 'N/A'}</span>
            </KTTooltip>
          );
        },
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MST/CCCD',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('MST/CCCD')}
          </span>
        ),
        width: '160px',
        selector: (row) =>
          row.type === AppConstants.accountType.individual
            ? row.idCardNumber?.length > 0
              ? row.idCardNumber
              : row.taxCode?.length > 0
                ? row.taxCode
                : ''
            : row.taxCode?.length > 0
              ? row.taxCode
              : '',
        style: tableStyle.columns.normal,
      },
      {
        id: 'TOTAL_REVENUE',
        sortable: false,
        name: UIHelper.buildTableColumn('Tổng doanh thu', 'VNĐ'),
        width: '160px',
        selector: (row) => Utils.formatNumber(row?.totalRevenue ?? 0),
        style: tableStyle.columns.normal,
        right: true,
      },
      {
        id: 'TOTAL_INVESTMENT',
        sortable: false,
        name: UIHelper.buildTableColumn('Tổng tiền đầu tư', 'VNĐ'),
        width: '160px',
        selector: (row) => Utils.formatNumber(row?.totalInvestment ?? 0),
        style: tableStyle.columns.normal,
        right: true,
      },
      {
        id: 'STATUS',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('status')}
          </span>
        ),
        minWidth: '150px',
        selector: (row) => {
          return (
            <KTLabel
              text={t(`agencyStatus_${row.status}`)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${DealerHelper.getAgencyStatusColor(row.status)}`}
            />
          );
        },
      },
      {
        id: 'COUNTRY',
        name: UIHelper.buildTableColumn('Quốc gia'),
        selector: (row) => _.find(AgencyHelper.Data.CountryOptions, { value: row?.country })?.name,
        style: tableStyle.columns.normal,
        width: '150px',
      },
      {
        id: 'Địa chỉ',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Địa chỉ')}
          </span>
        ),
        selector: (row) => {
          const area = ChargeHelper.Utils.getAreaById([areaTree], row?.areaId);
          let parentArea;
          if (area?.typeCode === 'W') {
            parentArea = ChargeHelper.Utils.getAreaById([areaTree], area?.parentId);
          }
          const addressResuslt = [
            row.address,
            area?.name,
            parentArea?.name
          ]
            .filter(Boolean)
            .join(', ');
          return (
            <KTTooltip text={addressResuslt} offset={[0, 0]}>
              <span>{addressResuslt?.length > 0 ? addressResuslt : ''}</span>
            </KTTooltip>
          );
        },
        minWidth: '250px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CREATEDBY',
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Người tạo')}
          </span>
        ),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row?.createdBy?.fullname ?? row?.name}>
              <span
                className="cursor-pointer hover-opacity-60"
                onClick={() => {
                  DealerHelper.navigateToParentAgency(row?.createdBy ?? row);
                }}
              >
                {row?.createdBy?.fullname ?? row?.name}
              </span>
            </KTTooltip>
          );
        },
        style: tableStyle.columns.link,
        sortable: false,
        width: '200px',
      },
      {
        id: 'Ngày tạo',
        sortable: false,
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Ngày tạo')}
          </span>
        ),
        selector: (row) => row?.createdAt ? Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm') : '',
        style: tableStyle.columns.normal,
        width: '180px',
      },
      {
        name: (
          <span
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
            className="letter-spacing-unset"
          >
            {t('Thao tác')}
          </span>
        ),
        width: '130px',
        wrap: true,
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return (
            <MenuActionDropdown
              menuAction={[
                {
                  iconElement: <i className="fa-regular fa-eye" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Xem chi tiết'),
                  click: () => {
                    router.navigate(`${row?.agencyId}`);
                  },
                  show: Utils.checkPermission(permissionList, AppConstants.permissionCode.groupView.code),
                },
                {
                  iconElement: <i className="fa-regular fa-bolt" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Kích hoạt tài khoản'),
                  click: () => {
                    DealerHelper.handleAcceptAgency(row, () => {
                      getInvestorGroupList(filter);
                    });
                  },
                  show: row?.status === 'REGISTERED'
                    && (adminRole ||
                      DealerHelper.checkHasEditPermission(
                        row,
                        row?.parentAgency,
                        user?.agency
                      ))
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.groupUpdate.code),
                },
                {
                  iconElement: <i className="fa-regular fa-pen-to-square" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('edit'),
                  click: () => {
                    handleShowEditAgency(row);
                  },
                  show: (!row?.numberOfSignedContracts
                    && DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ) || adminRole)
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.groupUpdate.code),
                },
                {
                  iconElement: <i className="fa-regular fa-plus-circle" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('addOrder'),
                  click: () => {
                    if (row?.numberOfSignedContracts && row?.numberOfSignedContracts > 0) {
                      router.navigate(`add-order?group=${row?.agencyId}`);
                    } else {
                      DealerHelper.showDialogCreateContract(row);
                    }
                  },
                  show: row?.status === 'NORMAL'
                    && (adminRole || DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ))
                    && user?.agency?.primaryRole === 'SALE'
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.groupUpdate.code),
                },
                {
                  iconElement: <i className="far fa-square" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Ngừng hoạt động'),
                  click: () => {
                    DealerHelper.handleStopAgency(row, () => {
                      getInvestorGroupList(filter);
                    });
                  },
                  show: row?.status === 'NORMAL'
                    && (adminRole || DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ))
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.groupUpdate.code),
                },
                {
                  iconElement: <i className="far fa-play" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Khôi phục hoạt động'),
                  click: () => {
                    DealerHelper.handleActiveAgency(row, () => {
                      getInvestorGroupList(filter);
                    });
                  },
                  show: row?.status === 'SUSPENDED'
                    && (adminRole || DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ))
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.groupUpdate.code),
                },
                {
                  iconElement: (
                    <i
                      className="fa-regular fa-file-contract"
                      style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                    ></i>
                  ),
                  text: 'Tạo hợp đồng CTV',
                  click: () => {
                    // AgencyHelper.UI.showDialogConfirmCreateContractWithSale(row, () => {});
                    AgencyHelper.UI.showDialogConfirmCreateContractWithAgency(
                      row,
                      () => { },
                      undefined,
                      async () => {
                        const hasSaleCommission =
                          row && row.commissions
                            ? await row.commissions.some(
                              (commission) => commission?.role === 'SALE'
                            )
                            : false;

                        if (!hasSaleCommission) {
                          await handleCreateSaleCommission(row);
                        }
                      }
                    );
                  },
                  show:
                    row?.status !== 'REGISTERED'
                    && adminRole
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.groupUpdate.code),
                },
                {
                  iconElement: <i className={`fa-regular fa-lock`} style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Đặt lại mật khẩu'),
                  click: () => {
                    DealerHelper.showDialogChangePassword(row, () => {
                      getInvestorGroupList(filter);
                    })
                  },
                  show: adminRole && Utils.checkPermission(permissionList, AppConstants.permissionCode.groupUpdate.code),
                },
                {
                  iconElement: <i className={`fa-regular fa-trash-can`} style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Xóa nhà đầu tư'),
                  click: () => {
                    DealerHelper.handleDeleteAgency(row, () => {
                      getInvestorGroupList(filter);
                    });
                  },
                  show: !row?.numberOfSignedContracts
                    && (adminRole || DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ))
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.groupDelete.code),
                },
              ]}
            />
          );
        },
      },
    ];
  }, [t, areaTree, user, filter]);

  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    saveChanges,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  useEffect(() => {
    getInvestorGroupList({ ...filter, parentAgencyId: adminRole ? '' : user?.agency?.agencyId });
  }, [filter]);

  const getInvestorGroupList = () => {
    try {
      DealerHelper.callApiGetAgencyList(filter);
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.reason);
    }
  };

  const bankList = useRef([]);
  // useEffect(() => {
  //   const getBankList = async () => {
  //     const url = '/misc/banks';
  //     const res = await axiosClient.get(url, {
  //       baseURL: `${AppConfigs.env.baseUrl}api/v1`,
  //     });
  //     const { result, banks } = res;
  //     if (result === 'success') {
  //       bankList.current = banks;
  //     }
  //   };

  //   getBankList();
  // }, []);

  // // MARK: ---- methods ----
  // const handleAddNewInvestorGroup = (row) => {
  //   AppCustomDialogHelper.show(
  //     _.isUndefined(row)
  //       ? 'Thêm nhà đầu tư hợp tác kinh doanh'
  //       : 'Cập nhật thông tin nhà đầu tư hợp tác kinh doanh',
  //     <ViewAddInvestorGroup
  //       row={row}
  //       onClosed={() => {
  //         AppCustomDialogHelper.hide();
  //       }}
  //       onSubmitted={async (params) => {
  //         const formattedParams = {
  //           ...params,
  //           operatorId: user?.agency?.operatorId,
  //           parentAgencyId: user?.agency?.agencyId,
  //         };
  //         if (_.isUndefined(row)) {
  //           // goi api tao AGENCY
  //           try {
  //             const res = await axiosClient.post('/agency/create', formattedParams);
  //             const { result, agency } = res;
  //             if (result === 'success') {
  //               ToastHelper.showSuccess('Thêm nhà đầu tư hợp tác kinh doanh thành công');
  //               setFilter({ ...filter });
  //               AppCustomDialogHelper.hide();
  //             } else {
  //               ToastHelper.showError(reason ?? 'Lỗi Thêm nhà đầu tư hợp tác kinh doanh');
  //             }
  //           } catch (error) {
  //             ToastHelper.showError(error?.response?.data?.reason);
  //           }
  //         } else {
  //           // goi api cap nhat AGENCY
  //           try {
  //             const res = await axiosClient.put(`/agency/${row?.agencyId}`, formattedParams);
  //             const { result } = res;
  //             if (result === 'success') {
  //               ToastHelper.showSuccess(
  //                 'Cập nhật thông tin nhà đầu tư hợp tác kinh doanh thành công'
  //               );
  //               setFilter({ ...filter });
  //               AppCustomDialogHelper.hide();
  //             } else {
  //               ToastHelper.showError(
  //                 reason ?? 'Lỗi Cập nhật thông tin nhà đầu tư hợp tác kinh doanh'
  //               );
  //             }
  //           } catch (error) {
  //             ToastHelper.showError(error?.response?.data?.reason);
  //           }
  //         }
  //       }}
  //       bankList={bankList.current}
  //     />,
  //     'lg',
  //     false
  //   );
  // };

  const handleOpenAddInvestorGroupModal = () => {
    BreadcrumbHelper.setBreadcrumPath(t('Thêm NĐT HTKD'), `${router?.pathname}/add`);
    router.navigate('add');
  };

  const handleShowEditAgency = (row) => {
    BreadcrumbHelper.setBreadcrumPath(
      t('Chỉnh sửa NĐT HTKD'),
      `${router?.pathname}/${row?.agencyId}/edit`
    );
    router.navigate(`${row?.agencyId}/edit`);
  };

  const handleCreateSaleCommission = async (row) => {
    await DealerHelper.callApiCreateAgencyCommission(
      Utils.transformEmptyStrings({
        operatorId: operatorId,
        agencyId: row?.agencyId,
        role: 'SALE',
        energyCommissionMode: AppConstants.energyCommissionMode.fixedPerKwh,
        energyCommissionValue: 0,
        energyCommissionDuration: null,
        serviceCommission: 0,
        saleCommission: [
          {
            revenue: 0,
            discount: Number(commissionRateSale),
          },
        ],
        packageCommission: [
          {
            revenue: 0,
            discount: Number(commissionRateGroup),
          },
        ],
        landlordCommission: JSON.parse(commissionRateLandlord),
      })
    );
    getInvestorGroupList();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NORMAL':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'WITHDRAWED':
        return 'primary';
      case 'TRANSFERED':
        return 'primary';
      default:
        return 'primary';
    }
  };

  const updateFilter = (key, value) => {
    Global.g_filterInvestorGroupList = { ...Global.g_filterInvestorGroupList, [key]: value };
    setFilter(Global.g_filterInvestorGroupList);
    setFilterDropdown(Global.g_filterInvestorGroupList);
  };

  const FilterComponentList = ({ isInDropdown = false }) => (
    <div className={`${isInDropdown ? 'row' : 'w-100'} d-flex  flex-row  flex-wrap`}>
      {/* loai nha dau tu */}
      <KTDropdownFilter
        label={t('Loại nhà đầu tư')}
        filterKey="type"
        options={DealerHelper.filterAgencyTypeOptions()}
        selectedValue={!isInDropdown ? Global.g_filterInvestorGroupList.type : filterDropdown.type}
        isInDropdown={isInDropdown}
        additionalClassName={`${isInDropdown ? 'pr-2 pt-4' : 'pl-0 pr-4'}`}
        handleChange={(value) => {
          if (isInDropdown) {
            setFilterDropdown({ ...filterDropdown, type: value });
          } else {
            updateFilter('type', value);
            updateFilter('page', 0);
          }
        }}
      />

      {!isInDropdown && !adminRole && (
        <KTDropdownFilter
          label={t('Đối tượng quản lý')}
          filterKey="deepLevel"
          options={DealerHelper.filterLevelOptions('GROUP', user?.agency?.deepLevel)}
          selectedValue={filterDropdown.deepLevel}
          isInDropdown={isInDropdown}
          additionalClassName="pr-4"
          handleChange={(value) => {
            if (isInDropdown) {
              setFilterDropdown({ ...filterDropdown, deepLevel: value });
            } else {
              updateFilter('deepLevel', value);
            }
          }}
        />
      )}

      {/* trang thai */}
      <KTDropdownFilter
        label={t('status')}
        filterKey="status"
        options={DealerHelper.filterStatusOptions()}
        selectedValue={
          !isInDropdown
            ? Global.g_filterInvestorGroupList.status ||
            (Global.g_filterInvestorGroupList.hasSignedContract ? AppConstants.agencyStatus.signed : '')
            : filterDropdown.status ||
            (Global.g_filterInvestorGroupList.hasSignedContract || filterDropdown.hasSignedContract ? AppConstants.agencyStatus.signed : '')
        }
        isInDropdown={isInDropdown}
        additionalClassName={`${isInDropdown ? 'pl-2 pt-4' : 'pl-0 pr-0 pr-xl-4'}`}
        handleChange={(value) => {
          const isSigned = value === 'SIGNED';
          const newStatus = isSigned ? '' : value;
          const newHasSignedContract = isSigned ? 1 : '';

          if (isInDropdown) {
            setFilterDropdown({
              ...filterDropdown,
              status: newStatus,
              hasSignedContract: newHasSignedContract,
            });
          } else {
            updateFilter('status', newStatus);
            updateFilter('hasSignedContract', newHasSignedContract);
            updateFilter('page', 0);
          }
        }}
      />

      {isInDropdown && (
        <>
          {!adminRole && (
            <KTDropdownFilter
              label={t('Đối tượng quản lý')}
              filterKey="deepLevel"
              options={DealerHelper.filterLevelOptions('GROUP', user?.agency?.deepLevel)}
              selectedValue={filterDropdown.deepLevel}
              isInDropdown={isInDropdown}
              additionalClassName="pr-2 pt-2"
              handleChange={(value) => {
                if (isInDropdown) {
                  setFilterDropdown({ ...filterDropdown, deepLevel: value });
                } else {
                  updateFilter('deepLevel', value);
                }
              }}
            />
          )}

          <KTDropdownFilter
            label={t('Người tạo')}
            filterKey="createdBy"
            options={DealerHelper.filterCreatedByOptions()}
            selectedValue={filterDropdown.createdBy}
            isInDropdown={isInDropdown}
            additionalClassName={adminRole ? 'pr-2 pt-2' : 'pl-2 pt-2'}
            handleChange={(value) => {
              if (isInDropdown) {
                setFilterDropdown({ ...filterDropdown, createdBy: value });
              } else {
                updateFilter('createdBy', value);
              }
            }}
          />

          {adminRole && (
            <div className="col-6 py-2 pl-2"></div>
          )}

          <div className="col-6 py-2 pr-2">
            <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
              {t('Khoảng thời gian thống kê')}
            </label>
            <CustomDateRangePicker
              toggleElementBorderRadius={5}
              alignRight={true}
              initialValue={
                filterDropdown.selectedValue.length > 0
                  ? filterDropdown.selectedValue
                  : dateFilter.tatCaThoiGian
              }
              onPressSave={(selectedValue, startDate, endDate) => {
                const updateFilterDropdown = {
                  ...filterDropdown,
                  from: startDate,
                  to: endDate,
                  selectedValue: selectedValue,
                };
                setFilterDropdown(updateFilterDropdown);
              }}
              filterComponent={filterDropdown}
            />
          </div>

          <KTDropdownFilter
            label='Quốc gia'
            filterKey="country"
            options={AgencyHelper.Data.CountryOptions}
            selectedValue={filterDropdown.country}
            additionalClassName="col-md-6 pl-2"
            handleChange={(value) => {
              setSelectedAreaProvince('');
              setSelectedAreaDistrict('');
              setFilterDropdown({ ...filterDropdown, country: value, areaId: '' });
            }}
          />

          <KTDropdownFilter
            label={t('city')}
            filterKey="areaId"
            options={areaProvinceOptions}
            selectedOption={selectedAreaProvince}
            selectedValue={filterDropdown.areaId}
            isInDropdown={isInDropdown}
            additionalClassName="pr-2 pt-2"
            searchable={true}
            handleChange={(value) => {
              const province = _.find(areaProvinceList, (item) => item.areaId === value);
              setSelectedAreaProvince(province);
              setSelectedAreaDistrict('');
              if (isInDropdown) {
                setFilterDropdown({ ...filterDropdown, areaId: value });
              } else {
                updateFilter('areaId', value);
              }
            }}
          />

          <KTDropdownFilter
            label={t('district')}
            filterKey="areaId"
            options={areaDistrictOptions}
            selectedOption={selectedAreaDistrict}
            selectedValue={filterDropdown.areaId}
            isInDropdown={isInDropdown}
            additionalClassName="pl-2 pt-2"
            searchable={true}
            handleChange={(value) => {
              setSelectedAreaDistrict(
                _.find(areaDistrictList, (item) => item.areaId === value)
              );
              if (isInDropdown) {
                setFilterDropdown({ ...filterDropdown, areaId: value });
              } else {
                updateFilter('areaId', value);
              }
            }}
          />
        </>
      )}
    </div>
  );

  // xuat file excel
  const handleExportExcel = async (exportData, params) => {
    let exportDataList = exportData;
    if (_.isEmpty(exportDataList)) {
      ToastHelper.showError('Không có dữ liệu để tải xuống');
      return;
    }

    const sheetFileName = 'Danh_sách_nhà_đầu_tư_HTKD_' + moment(params.from).format('DDMMYYYY') + '_' + moment(params.to).format('DDMMYYYY');
    const ext = '.xlsx';
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sheetFileName, {
      headerFooter: { firstHeader: sheetFileName },
    });

    sheet.mergeCells('A1:L1');
    sheet.getCell('A1').value = `DANH SÁCH NHÀ ĐẦU TƯ HTKD (NGÀY ${moment(params.from).format(
      'DD/MM/YYYY'
    )} - ${moment(params.to).format('DD/MM/YYYY')})`;
    sheet.getCell('A1').font = {
      name: 'Times New Roman',
      family: 4,
      size: 11,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    sheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
    sheet.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '29618E' },
      color: { argb: 'FFFFFF' },
    };
    sheet.getCell('A1').border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    sheet.getRow(1).height = 25;

    const columns = [
      { key: 'stt', width: 7 },
      { key: 'agencyCode', width: 18 },
      { key: 'agencyName', width: 35 },
      { key: 'type', width: 18 },
      { key: 'parentAgency', width: 22 },
      { key: 'phone', width: 22 },
      { key: 'email', width: 30 },
      { key: 'cccd', width: 22 },
      { key: 'status', width: 20 },
      { key: 'address', width: 35 },
      { key: 'createBy', width: 22 },
      { key: 'date', width: 22 },
    ];
    sheet.columns = columns;

    sheet.getRow(2).height = 30;
    sheet.getRow(2).font = {
      name: 'Times New Roman',
      family: 4,
      size: 11,
      bold: true,
    };
    sheet.getRow(2).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };

    sheet.getCell('A2').value = 'STT';
    sheet.getCell('B2').value = 'Mã NĐT';
    sheet.getCell('C2').value = 'Tên NĐT HTKD';
    sheet.getCell('D2').value = 'Loại nhà đầu tư';
    sheet.getCell('E2').value = 'Đối tượng quản lý';
    sheet.getCell('F2').value = 'Số điện thoại';
    sheet.getCell('G2').value = 'Email';
    sheet.getCell('H2').value = 'MST/CCCD';
    sheet.getCell('I2').value = 'Trạng thái';
    sheet.getCell('J2').value = 'Địa chỉ';
    sheet.getCell('K2').value = 'Người tạo';
    sheet.getCell('L2').value = 'Ngày tạo';


    ['A2:L2'].forEach((range) => {
      sheet.getRow(range.split(':')[0].slice(1)).eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    let i = 1;
    exportDataList?.forEach((row) => {
      // Dia chi
      const area = ChargeHelper.Utils.getAreaById([areaTree], row?.areaId);
      let parentArea;
      if (area?.typeCode === 'W') {
        parentArea = ChargeHelper.Utils.getAreaById([areaTree], area?.parentId);
      }
      const addressResuslt = [
        row.address,
        area?.name,
        parentArea?.name
      ]
        .filter(Boolean)
        .join(', ');

      const dataRow = sheet.addRow({
        stt: i++,
        agencyCode: row?.agencyCode ?? row.agencyId,
        agencyName: row?.name,
        type: t(row.type),
        parentAgency: row?.parentAgency?.name ?? '',
        phone: row.phone ?? '',
        email: row?.email ?? '',
        cccd: row.type === AppConstants.accountType.individual
          ? row.idCardNumber?.length > 0
            ? row.idCardNumber
            : row.taxCode?.length > 0
              ? row.taxCode
              : ''
          : row.taxCode?.length > 0
            ? row.taxCode
            : '',
        status: t(`agencyStatus_${row.status}`),
        address: addressResuslt?.length > 0 ? addressResuslt : '',
        createBy: row?.createdBy?.fullname ?? '',
        date: row?.createdAt ? Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm') : '',
      });

      dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        if (colNumber === 1) {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        }
      });
    });

    sheet.eachRow((row, rowNumber) => {
      if (rowNumber >= 3) {
        row.font = {
          name: 'Times New Roman',
          family: 4,
          size: 11,
          bold: false,
        };
      }
    });

    workbook.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = sheetFileName + ext;
      anchor.click();
      window.URL.revokeObjectURL(url);
      ToastHelper.showSuccess('Tải xuống file Excel thành công');
      AppCustomDialogHelper.hide();
    });
  };

  return (
    <div className='m-4'>
      <KTBSCard
        borderRadius="10"
        title={t('Danh sách nhà đầu tư HTKD')}
        description={
          <div>
            <span
              className="font-weight-bolder"
              style={{ color: AppColors.gray[800], fontSize: '14px' }}
            >
              {t('total')}:{' '}
            </span>
            <span style={{ color: AppColors.primary, fontSize: '14px', fontWeight: 600 }}>
              {groupTotal != 0 ? groupTotal : groupList.length} {t('nhà đầu tư HTKD')}
            </span>
          </div>
        }
        additionalHeaderClassName="pt-4 pb-2 "
        additionalBodyClassName="py-0 "
        additionalFooterClassName="py-4 "
        hideDividerHeader
        hideDividerFooter
        toolbar={
          <div className="d-flex gap-3">
            <KTBSButton
              color={KTBSButtonColor.primary}
              title={t('Tải xuống')}
              size={KTBSButtonSize.default}
              borderRadius={5}
              buttonTitleFontWeight="600"
              iconElement={<i className="fa-regular fa-download" style={{ fontSize: 15 }} />}
              onPress={async () => {
                try {
                  ChargeHelper.UI.Excel.showDialogDownloadExcelFile(
                    Global.g_filterInvestorGroupList,
                    async (params) => {
                      try {
                        const { agencies = [] } = await agencyApi.getAgencyList(params);
                        return handleExportExcel(agencies, params);
                      } catch (error) {
                        ToastHelper.showError(`Lỗi khi lấy dữ liệu: ${error.message}`);
                        return [];
                      }
                    },
                  );
                } catch (error) {
                  ToastHelper.showError(`Lỗi khi khởi tạo tải xuống: ${error.message}`);
                }
              }}
              height={43}
              buttonTitleFontSize={14}
            />
            {Utils.checkPermission(permissionList, AppConstants.permissionCode.groupCreate.code) && (
              <KTBSButton
                color={KTBSButtonColor.success}
                title={t('Thêm NĐT HTKD')}
                size={KTBSButtonSize.default}
                borderRadius={5}
                buttonTitleFontWeight="600"
                iconElement={<i className="fa-regular fa-circle-plus" />}
                onPress={handleOpenAddInvestorGroupModal}
              />
            )}
          </div>
        }
        subToolbar={
          <div className="w-100 d-flex align-items-end flex-wrap">
            <div className={`col-lg-3 pl-0 py-2 pr-0 pr-xl-4 pr-lg-4`}>
              <label htmlFor="search" className="mb-0 mr-2 font-weight-bolder">
                {t('search')}
              </label>
              <KTFormSearch
                name="search-bar"
                placeholder={t('Theo mã nđt, tên nđt, số điện thoại, email, MST/CCCD...)')}
                value={Global.g_filterInvestorGroupList.q}
                onSubmit={(text) => {
                  Global.g_filterInvestorGroupList = {
                    ...Global.g_filterInvestorGroupList,
                    q: text,
                    page: 0,
                  };
                  setFilter(Global.g_filterInvestorGroupList);
                  setFilterDropdown(Global.g_filterInvestorGroupList);
                }}
              />
            </div>
            <div className="col-xl-7 col-lg-9 px-0">
              <FilterComponentList />
            </div>

            {/* bo loc */}
            <div className="py-2 pr-4">
              <DropdownFilterTable
                filterComponent={[
                  <div className={`${'col-12 p-0'}`}>
                    <KTFormSearch
                      name="search-bar"
                      placeholder={t(
                        'Theo mã nđt, tên nđt, số điện thoại, email, MST/CCCD...)'
                      )}
                      value={filterDropdown.q}
                      onSubmit={(text) => {
                        const updateFilterDropdown = { ...filterDropdown, q: text };
                        setFilterDropdown(updateFilterDropdown);
                      }}
                    />
                  </div>,
                  <FilterComponentList
                    isInDropdown={true}
                    onChange={(filter) => {
                      setFilterDropdown({
                        ...filterDropdown,
                        ...filter,
                      });
                    }}
                  />,
                ]}
                resetDefaultTable={() => {
                  const updateFilterDropdown = {
                    ...filterDropdown,
                    q: '',
                    type: '',
                    status: '',
                    from: getDateFilterOption(dateFilter.tatCaThoiGian)[0].format('YYYY-MM-DD'),
                    to: getDateFilterOption(dateFilter.tatCaThoiGian)[1].format('YYYY-MM-DD'),
                    selectedValue: dateFilter.tatCaThoiGian,
                    areaId: '',
                    createdBy: '',
                    deepLevel: '',
                    hasSignedContract: '',
                  };
                  setFilterDropdown(updateFilterDropdown);
                  setFilter(updateFilterDropdown);
                  Global.g_filterInvestorGroupList = updateFilterDropdown;
                  setSelectedAreaProvince('');
                  setSelectedAreaDistrict('');
                }}
                saveFilter={() => {
                  setFilter(filterDropdown);
                  Global.g_filterInvestorGroupList = filterDropdown;
                }}
                isDefaultFilter={Utils.checkDefaultFilter(
                  ['role', 'page', 'limit', 'from', 'to', 'selectedValue'],
                  filter
                )}
              />
            </div>
            {/* cau hinh cot */}
            <div className="pr-0 py-2">
              <DropdownTableConfigColumn
                columnIds={columnIds}
                fullColumns={fullColumns}
                fullColumnIds={fullColumnsIdSorted}
                onChangeCheckboxValue={onChangeCheckboxValue}
                saveChanges={saveChanges}
                resetDefaultTableColumn={() => {
                  setFullColumnIds(defaultColumnIds);
                  resetDefaultTableColumn();
                }}
                onChangeFullColumnIds={(ids) => {
                  setFullColumnIds(ids);
                  const key = prefKey + '_FULL_COLUMN_IDS';
                  localStorage.setItem(key, ids.join('|'));
                }}
                onSelectAll={onSelectAll}
                onUnSelectAll={onUnSelectAll}
              />
            </div>
          </div>
        }
        footer={
          <KTPagination
            currentPage={filter.page + 1}
            rowCount={groupTotal}
            rowsPerPage={filter.limit}
            currentItemInPageCount={groupList.length}
            onChangePage={(newPage) => {
              const iNewPage = parseInt(newPage);
              Global.g_filterInvestorGroupList = {
                ...Global.g_filterInvestorGroupList,
                page: iNewPage - 1,
              };
              setFilter(Global.g_filterInvestorGroupList);
            }}
            onChangeRowsPerPage={(newRowsPerPage) => {
              const iNewRowsPerPage = parseInt(newRowsPerPage);
              Global.g_filterInvestorGroupList = {
                ...Global.g_filterInvestorGroupList,
                limit: iNewRowsPerPage,
                page: 0,
              };
              setFilter(Global.g_filterInvestorGroupList);
            }}
            loading={loading}
            color={KTPaginationColor.success}
            appendText="nhà đầu tư HTKD"
          />
        }
        body={
          <KTTable
            columns={columns}
            items={groupList}
            selectableRows={false}
            striped={false}
            compactGrid={false}
            onClickedRow={() => { }}
            onSelectedRowsChanged={(rows) => { }}
            onDoubleClickedRow={(rows) => { }}
            noDataComponent={
              <EmptyView
                icon={imgEmptyNoData}
                title="Không có dữ liệu để hiển thị"
                text={<span>Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.</span>}
              />
            }
          />
        }
      />
      {showModelConfigInvestorTableColumn && (
        <ModelConfigInvestorTableColumn
          dialogShowing={showModelConfigInvestorTableColumn}
          handleClose={() => setShowModelConfigInvestorTableColumn(false)}
          handleSubmit={() => {
            setShowModelConfigInvestorTableColumn(false);
          }}
          setFilter={setFilter}
          filter={Global.g_filterInvestorGroupList}
        />
      )}
    </div>
  );
}

export default InvestorGroupListScreen;
